import type { QueryObserverResult } from '@tanstack/react-query'
import type { AxiosError, AxiosResponse } from 'axios'
import type {
  InsuranceParamsDto,
  InsuranceParamsLeasingDto,
  InsuranceParamsPropertyDto,
  InsuranceParamsTravelDto,
} from 'BrokerTsLib/src/dtos'
import type {
  ReactNode
} from 'react'
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'

import { useApp } from '@/hooks/useApp'
import { useNavigateToNotFound, useNavigateToUnauthorized, useNavigateToUnauthorizedBmw } from '@/hooks/useCustomNavigate'


import { Apps } from '@/dtos'
import { useCreateInsuranceParamsFromDraft } from '@/hooks/useInsuranceDraft'
import { useGetInsuranceParams } from '../hooks/useWizard'
import { useAuth } from './AuthContext'
import { usePartner } from './PartnerContext'

export type IInsuranceParamsContext = {
  isLoadingData: boolean
  isFetchingData: boolean
  insuranceParamsId: string | undefined
  /** Nastaví aktivní InsuranceParamsId programově (bez změny URL) – např. po částečně vytvořeném skladu. */
  setInsuranceParamsId: (id?: string) => void
  refetchInsuranceParams: () => Promise<
    QueryObserverResult<
      AxiosResponse<
        | InsuranceParamsDto
        | InsuranceParamsLeasingDto
        | InsuranceParamsTravelDto
        | InsuranceParamsPropertyDto,
        any
      >,
      any
    >
  >
  insuranceParams?:
  | InsuranceParamsDto
  | InsuranceParamsLeasingDto
  | InsuranceParamsTravelDto
  | InsuranceParamsPropertyDto
  triggerNewInsurance: boolean
  contractReplacement: boolean
  clearTriggerNewInsurance: () => void
  clearContractReplacement: () => void
  error?: AxiosError | null
  isDisabled: boolean
  isContracted: boolean
  isSubaruInsurance: boolean
  isMazdaInsurance: boolean
  isKiaInsurance: boolean
  isMgInsurance: boolean
}
const insuranceParamsContext = createContext<IInsuranceParamsContext>(
  {} as IInsuranceParamsContext,
)

export const InsuranceParamsProvider = ({
  insuranceParamsId = undefined,
  children,
}: {
  insuranceParamsId?: string
  children: ReactNode
}) => {
  const { insuranceParamsId: insuranceParamsRd_Params } = useParams()

  const [searchParams, setSearchParams] = useSearchParams()
  const auth = useAuth()
  const { currentPartner } = usePartner()
  const { app } = useApp()

  // Programově nastavené id (bez URL) – má přednost; využívá resume flow skladového pojištění
  const [overrideParamsId, setOverrideParamsId] = useState<string | undefined>(undefined)
  const paramsId = overrideParamsId ?? insuranceParamsId ?? insuranceParamsRd_Params

  const navigate = useNavigate()
  const navigateToUnauthorized = useNavigateToUnauthorized()
  const navigateToNotFound = useNavigateToNotFound()
  const navigateToUnauthorizedBmw = useNavigateToUnauthorizedBmw(paramsId)

  const refetchCount = useRef(0)
  const {
    isLoading,
    isFetching,
    data: insuranceParams,
    refetch,
    error,
    ...rrrr
  } = useGetInsuranceParams(paramsId ?? '', {
    enabled: !!paramsId || app == Apps.publicPoleas, // poleas ma paramsId v cookie
    gcTime: 0,
  })

  useEffect(() => {
    if (!error) return
    const { response } = error as AxiosError
    if (response?.status == 400) navigateToNotFound()
    else if (response?.status == 403) {
      if (navigateToUnauthorizedBmw)
        navigateToUnauthorizedBmw()
      else
        navigateToUnauthorized('Access to InsuranceParams denied')
    }
    else
      if (refetchCount.current < 3) {
        refetchCount.current++
        setTimeout(() => refetch(), (refetchCount.current + 1) * 2000)
      }
  }, [error])

  useEffect(() => {
    if (insuranceParams) {
      refetchCount.current = 0
    }
  }, [insuranceParams])

  const isContracted =
    insuranceParams?.data.generalParams?.paramsState === 'Contracted' || !!insuranceParams?.data.generalParams?.contractNumber
  const hasPpzAuthorization = auth.user?.autorizacePpz?.hasAuthorization

  const isDisabled =
    isContracted || (currentPartner?.partner === 'Jap' && !hasPpzAuthorization && app != Apps.publicPoleas) || (app == Apps.leasing && !auth.user?.adminCebiaRights)

  const triggerNewInsurance = searchParams.get('new') !== undefined && searchParams.get('new') !== null
  const clearTriggerNewInsurance = () => {
    searchParams.delete('new')
    setSearchParams(searchParams)
  }

  const contractReplacement = searchParams.get('contractReplacement') !== undefined && searchParams.get('contractReplacement') !== null
  const clearContractReplacement = () => {
    searchParams.delete('contractReplacement')
    setSearchParams(searchParams)
  }

  const { mutateAsync: createInsuranceParamsFromDraftAsync } = useCreateInsuranceParamsFromDraft()
  const createInsuranceParamsFromDraftCalled = useRef(false)

  useEffect(() => {
    const draft = searchParams.get('draft')
    if (draft && !createInsuranceParamsFromDraftCalled.current) {
      createInsuranceParamsFromDraftCalled.current = true
      createInsuranceParamsFromDraftAsync(draft)
        .then((response) => {
          navigate(`1/${response.data}`)
        })
    }
  }, [searchParams, createInsuranceParamsFromDraftAsync, navigate])

  return (
    <insuranceParamsContext.Provider
      value={{
        insuranceParamsId: paramsId,
        setInsuranceParamsId: setOverrideParamsId,
        isLoadingData: isLoading,
        isFetchingData: isFetching,
        refetchInsuranceParams: refetch,
        insuranceParams: insuranceParams?.data,
        triggerNewInsurance,
        clearTriggerNewInsurance,
        contractReplacement,
        clearContractReplacement,
        error: error,
        isDisabled,
        isContracted,
        isSubaruInsurance: insuranceParams?.data.vehicle?.subaruConditionsFulfilled ?? false,
        isMazdaInsurance: insuranceParams?.data.vehicle?.mazdaConditionsFulfilled ?? false,
        isKiaInsurance: insuranceParams?.data.vehicle?.kiaConditionsFulfilled ?? false,
        isMgInsurance: insuranceParams?.data.vehicle?.mgConditionsFulfilled ?? false
      }}
    >
      {children}
    </insuranceParamsContext.Provider>
  )
}

export const useInsuranceParams = () => {
  const context = useContext(insuranceParamsContext)

  return context
}
